// OpenAI service for medical recommendations
class OpenAIService {
  constructor() {
    // Use CloudFlare Pages Function instead of direct API calls
    // This keeps API keys secure on the server
    this.baseURL = "/api/chat"; // CloudFlare Pages Function endpoint

    console.log("OpenAI Service initialized with secure proxy");
  }

  async generateMedicalRecommendation(patientData) {
    const prompt = this.buildMedicalPrompt(patientData);
    return this.callOpenAI(prompt);
  }

  async generateRefinedAnalysis(patientData, questions, answers) {
    const prompt = this.buildRefinedAnalysisPrompt(
      patientData,
      questions,
      answers
    );
    return this.callOpenAI(prompt, "refined");
  }

  async callOpenAI(prompt, analysisType = "main") {
    try {
      // Call CloudFlare Pages Function (which proxies to OpenAI)
      const response = await fetch(this.baseURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content:
                "You are an expert medical AI assistant providing clinical decision support to licensed physicians. Provide direct, evidence-based medical analysis while maintaining appropriate clinical disclaimers.",
            },
            {
              role: "user",
              content: prompt,
            },
          ],
          max_tokens: 1500, // Increased to handle complex analyses
          temperature: 0.3, // Lower temperature for more consistent medical advice
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const content =
        data.choices[0]?.message?.content || "No recommendation generated";

      // Parse and validate JSON response
      try {
        // Preprocess content to handle markdown code blocks
        let jsonContent = content.trim();

        // Check if content is wrapped in markdown code blocks
        if (
          jsonContent.startsWith("```json") ||
          jsonContent.startsWith("```")
        ) {
          // Extract JSON from markdown code blocks
          const lines = jsonContent.split("\n");
          const startIdx = lines.findIndex((line) =>
            line.trim().startsWith("```")
          );
          const endIdx = lines.findLastIndex((line) => line.trim() === "```");

          if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
            jsonContent = lines
              .slice(startIdx + 1, endIdx)
              .join("\n")
              .trim();
          } else if (startIdx !== -1) {
            // Handle case where closing ``` is missing (truncated response)
            jsonContent = lines
              .slice(startIdx + 1)
              .join("\n")
              .trim();
            console.warn(
              "Response appears to be truncated - missing closing code block"
            );
          }
        }

        // Attempt to fix common JSON truncation issues
        if (!jsonContent.endsWith("}")) {
          // Try to complete incomplete JSON by adding closing braces
          const openBraces = (jsonContent.match(/{/g) || []).length;
          const closeBraces = (jsonContent.match(/}/g) || []).length;
          const missingBraces = openBraces - closeBraces;

          if (missingBraces > 0) {
            // Remove any incomplete trailing content
            const lastCompleteComma = jsonContent.lastIndexOf(",");
            const lastCompleteBrace = jsonContent.lastIndexOf("}");
            const cutPoint = Math.max(lastCompleteComma, lastCompleteBrace);

            if (cutPoint > 0) {
              jsonContent = jsonContent.substring(0, cutPoint + 1);
              // Add missing closing braces
              jsonContent += "}".repeat(missingBraces);
              console.warn("Attempted to fix truncated JSON response");
            }
          }
        }

        console.log(
          "Attempting to parse JSON content:",
          jsonContent.substring(0, 200) + "..."
        );
        const analysis = JSON.parse(jsonContent);

        // Validate required fields based on analysis type
        if (analysisType === "refined") {
          // Validation for refined analysis
          if (!analysis.refinedInsights) {
            throw new Error(
              "Invalid refined analysis structure received from AI"
            );
          }
        } else {
          // Validation for main analysis
          if (
            !analysis.differentialDiagnosis ||
            !analysis.clinicalPatterns ||
            !analysis.riskStratification
          ) {
            throw new Error("Invalid analysis structure received from AI");
          }

          // Ensure followUpQuestions is an array (can be empty)
          if (!Array.isArray(analysis.followUpQuestions)) {
            analysis.followUpQuestions = [];
          }
        }

        console.log("Successfully parsed AI analysis");
        return analysis;
      } catch (parseError) {
        console.error(
          "Failed to parse AI response as JSON, falling back to text:",
          parseError
        );
        console.error("Original content length:", content.length);
        console.error("Content preview:", content.substring(0, 300) + "...");

        // Return a fallback structure with the raw content
        return {
          error: true,
          rawContent: content,
          message:
            "AI returned unstructured response. Check console for details.",
        };
      }
    } catch (error) {
      console.error("OpenAI API Error:", error);
      throw new Error(
        `Failed to generate medical recommendation: ${error.message}`
      );
    }
  }

  buildMedicalPrompt(patient) {
    const formatVitalSigns = (vitals) => {
      if (!vitals || Object.keys(vitals).length === 0) return "Not recorded";

      const signs = [];
      if (vitals.temp) signs.push(`Temperature: ${vitals.temp}°C`);
      if (vitals.bp) signs.push(`BP: ${vitals.bp}`);
      if (vitals.hr) signs.push(`HR: ${vitals.hr} bpm`);
      if (vitals.spo2) signs.push(`SpO2: ${vitals.spo2}%`);
      if (vitals.weight) signs.push(`Weight: ${vitals.weight} kg`);

      return signs.join(", ") || "Not recorded";
    };

    const visitHistory =
      patient.visits
        ?.map((visit, index) => {
          const visitDate = new Date(visit.date);
          const complaints = visit.complaints?.length
            ? visit.complaints.join(", ")
            : "None recorded";
          const allergies = visit.allergies?.length
            ? visit.allergies.join(", ")
            : "None known";
          const pastHistory = visit.pastHistory?.length
            ? visit.pastHistory.join(", ")
            : "None recorded";
          const currentMeds = visit.takenDrugs?.length
            ? visit.takenDrugs.join(", ")
            : "None";
          const prescriptions = visit.prescription?.length
            ? visit.prescription
                .map(
                  (p) =>
                    `${p.name} ${p.dose}${p.unit}${
                      p.duration ? ` for ${p.duration}` : ""
                    }`
                )
                .join(", ")
            : "None";
          const administeredMeds = visit.administeredMeds?.length
            ? visit.administeredMeds
                .map(
                  (m) =>
                    `${m.name} ${m.dose}${m.unit} (${m.route})${
                      m.time ? ` at ${m.time}` : ""
                    }`
                )
                .join(", ")
            : "None";

          return `Visit ${
            index + 1
          } (${visitDate.toLocaleDateString()}): Chief Complaints: ${complaints}, Vital Signs: ${formatVitalSigns(
            visit.vitalSigns
          )}, Allergies: ${allergies}, Past History: ${pastHistory}, Current Medications: ${currentMeds}, Prescriptions: ${prescriptions}, Administered: ${administeredMeds}, Notes: ${
            visit.notes || "None"
          }`;
        })
        .join(". ") || "No visits recorded";

    return `You are analyzing a patient case for a licensed physician. Provide a direct, comprehensive clinical analysis.

PATIENT: ${patient.age} year old ${patient.gender}, Status: ${
      patient.status
    }, Total Visits: ${patient.visits?.length || 0}, Patient Since: ${new Date(
      patient.createdAt
    ).toLocaleDateString()}

VISIT HISTORY: ${visitHistory}

ANALYSIS REQUEST: Provide a structured medical analysis. Be direct and specific in your clinical assessments. Use EXACTLY this JSON format:

{
  "differentialDiagnosis": [
    {
      "condition": "Condition Name",
      "confidence": "high|medium|low",
      "reasoning": "Brief explanation for this diagnosis"
    }
  ],
  "clinicalPatterns": "Identify significant trends, recurring symptoms, or concerning patterns across visits",
  "riskStratification": {
    "cardiovascular": {
      "level": "high|medium|low",
      "details": "Assessment details"
    },
    "infectious": {
      "level": "high|medium|low", 
      "details": "Assessment details"
    },
    "metabolic": {
      "level": "high|medium|low",
      "details": "Assessment details"
    },
    "neurological": {
      "level": "high|medium|low",
      "details": "Assessment details"
    }
  },
  "immediateConcerns": [
    {
      "concern": "Specific concern",
      "urgency": "high|medium|low",
      "action": "Recommended immediate action"
    }
  ],
  "investigations": [
    {
      "test": "Test name",
      "priority": "urgent|routine",
      "indication": "Why this test is needed"
    }
  ],
  "specialistReferrals": [
    {
      "specialty": "Specialist type",
      "urgency": "urgent|routine",
      "indication": "Clinical reason for referral"
    }
  ],
  "monitoring": {
    "followUpInterval": "Recommended follow-up timeframe",
    "parameters": ["Parameter 1", "Parameter 2"],
    "redFlags": ["Warning sign 1", "Warning sign 2"]
  },
  "preventiveMeasures": [
    {
      "measure": "Preventive action",
      "timeline": "When to implement",
      "indication": "Age/gender appropriate reason"
    }
  ],
  "followUpQuestions": [
    {
      "id": "question_1",
      "question": "Specific question to clarify diagnosis",
      "type": "dropdown|text|toggle",
      "options": ["Option 1", "Option 2", "Option 3"],
      "rationale": "Why this question helps with decision-making"
    }
  ]
}

Follow-up Questions: Generate 0-3 decisive questions only if they would significantly impact diagnosis or treatment decisions. Question types:
- "dropdown": Single-select with 2-5 options in "options" array
- "text": Free text input (200 char limit)  
- "toggle": Yes/No question (no options array needed)

CRITICAL: Return ONLY valid JSON without markdown formatting. Use the exact field names shown above. Keep response concise to avoid truncation.`;
  }

  buildRefinedAnalysisPrompt(patient, questions, answers) {
    const qaContext = questions
      .map((q, index) => {
        const answer = answers[q.id] || "Not answered";
        return `Q: ${q.question}\nA: ${answer}`;
      })
      .join("\n\n");

    return `Based on the previous analysis and the doctor's responses below, provide focused insights and refined assessment.

ORIGINAL PATIENT: ${patient.age} year old ${patient.gender}, Status: ${patient.status}

DOCTOR'S RESPONSES TO FOLLOW-UP QUESTIONS:
${qaContext}

TASK: Generate a refined assessment based on this new information. Focus on how the answers impact the diagnosis and treatment approach. Return JSON in this exact format:

{
  "refinedInsights": "Focused analysis based on the doctor's responses",
  "updatedDifferentialDiagnosis": [
    {
      "condition": "Condition Name",
      "confidence": "high|medium|low",
      "changeFromOriginal": "What changed and why"
    }
  ],
  "specificRecommendations": [
    {
      "action": "Specific action based on answers",
      "priority": "urgent|routine",
      "reasoning": "Why this is recommended based on responses"
    }
  ],
  "keyInsights": [
    "Insight 1 based on doctor's answers",
    "Insight 2 based on doctor's answers"
  ]
}

CRITICAL: Return ONLY valid JSON. Keep focused and concise. Only include information that is directly influenced by the doctor's responses.`;
  }
}

export default new OpenAIService();
